// Tipos de cambio y diferencial cambiario (NIC 21). Financiero → Configuración.
//
// Tres cosas que son la misma historia contada en tres momentos:
//   · las TASAS con las que se convierte a la moneda funcional,
//   · el diferencial REALIZADO (se facturó a una tasa y se cobró a otra),
//   · la REVALUACIÓN de lo que sigue abierto al cierre (diferencial no realizado).
//
// Las dos últimas generan asientos contables, así que ambas se ven primero y se
// contabilizan después, nunca de un solo clic.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeftRight, Calculator, Loader2, Plus, Scale } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { fechaLocal } from '@/lib/utils/fecha';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const rpc = async <T,>(fn: string, args: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post<T>(`/rpc/${fn}`, args, FIN)).data;

const field = 'flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const num = (v: unknown, dec = 2) =>
  (Number(v) || 0).toLocaleString(LOCALE, { minimumFractionDigits: dec, maximumFractionDigits: dec });
const inicioMes = () => fechaLocal().slice(0, 8) + '01';

interface Tasa { moneda: string; fecha: string; tasa: number; fuente: string | null; funcional: string | null }
interface Realizado {
  lado: string; abono_id: string; fecha: string; monto: number; tc_abono: number | null;
  moneda: string; tc_doc: number; documento: string | null; tercero: string | null; diferencial: number;
}
interface Revaluable {
  lado: string; ticket_id: string; documento: string | null; moneda: string; tercero: string | null;
  saldo_me: number; tasa_previa: number; tasa_cierre: number | null; diferencial: number;
}

const Signo = ({ v }: { v: number }) => (
  <span className={Number(v) < 0 ? 'text-red-600' : Number(v) > 0 ? 'text-green-600' : ''}>
    {Number(v) > 0 ? '+' : ''}{num(v)}
  </span>
);
const Lado = ({ lado }: { lado: string }) => (
  <span className={`px-1.5 py-0.5 rounded text-[11px] ${lado === 'cxc' ? 'bg-blue-500/10 text-blue-600' : 'bg-amber-500/10 text-amber-600'}`}>
    {lado === 'cxc' ? 'Por cobrar' : 'Por pagar'}
  </span>
);

// ------------------------------------------------------------- tasas -------
function Tasas() {
  const qc = useQueryClient();
  const [moneda, setMoneda] = useState('');
  const [form, setForm] = useState({ moneda: 'USD', fecha: fechaLocal(), tasa: '' });

  const { data: tasas = [], isFetching } = useQuery<Tasa[]>({
    queryKey: ['fin-tc', moneda],
    queryFn: () => rpc<Tasa[]>('fin_tc_listar', { _moneda: moneda || null, _limite: 200 }),
  });
  const funcional = tasas[0]?.funcional ?? null;

  const registrar = useMutation({
    mutationFn: () => rpc('tc_registrar', {
      _moneda: form.moneda.toUpperCase(), _fecha: form.fecha, _tasa: Number(form.tasa), _fuente: 'manual',
    }),
    onSuccess: () => {
      toast.success('Tipo de cambio registrado');
      setForm((f) => ({ ...f, tasa: '' }));
      void qc.invalidateQueries({ queryKey: ['fin-tc'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const valido = form.moneda.trim().length === 3 && Number(form.tasa) > 0 && !!form.fecha;
  const mismaFuncional = funcional && form.moneda.toUpperCase() === funcional;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Registrar una tasa</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col text-xs text-muted-foreground">Moneda
              <input className={`${field} w-24 mt-1 uppercase`} maxLength={3} value={form.moneda}
                onChange={(e) => setForm((f) => ({ ...f, moneda: e.target.value.toUpperCase() }))} /></label>
            <label className="flex flex-col text-xs text-muted-foreground">Fecha
              <input type="date" className={`${field} w-40 mt-1`} value={form.fecha}
                onChange={(e) => setForm((f) => ({ ...f, fecha: e.target.value }))} /></label>
            <label className="flex flex-col text-xs text-muted-foreground">
              Tasa {funcional && <span className="normal-case">(1 {form.moneda || '···'} = ? {funcional})</span>}
              <input type="number" step="0.000001" min="0" className={`${field} w-40 mt-1`} value={form.tasa}
                onChange={(e) => setForm((f) => ({ ...f, tasa: e.target.value }))} /></label>
            <Button isLoading={registrar.isPending} disabled={!valido || !!mismaFuncional}
              onClick={() => registrar.mutate()}><Plus className="w-4 h-4 mr-1" /> Registrar</Button>
          </div>
          {mismaFuncional && (
            <p className="text-sm text-amber-600">
              {funcional} es la moneda funcional de esta empresa: no necesita tasa (siempre vale 1).
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Histórico {funcional && <span className="font-normal text-muted-foreground text-sm">— convertido a {funcional}</span>}</CardTitle>
          <div className="flex items-center gap-2">
            <input className={`${field} w-28 uppercase`} maxLength={3} placeholder="Filtrar"
              value={moneda} onChange={(e) => setMoneda(e.target.value.toUpperCase())} />
            {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">N°</th><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Moneda</th>
              <th className="py-2 pr-3 text-right">Tasa</th><th className="py-2 pr-3">Fuente</th>
            </tr></thead>
            <tbody>
              {tasas.map((t, i) => (
                <tr key={`${t.moneda}-${t.fecha}`} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-1.5 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-1.5 pr-3">{t.fecha}</td>
                  <td className="py-1.5 pr-3 font-mono">{t.moneda}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(t.tasa, 6)}</td>
                  <td className="py-1.5 pr-3 text-muted-foreground">{t.fuente ?? '—'}</td>
                </tr>
              ))}
              {!tasas.length && !isFetching && (
                <tr><td colSpan={5} className="py-10 text-center text-muted-foreground">
                  Aún no hay tasas registradas. Sin ellas no se puede revaluar ni calcular diferencial.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// --------------------------------------------------- diferencial realizado -
function RealizadoTab() {
  const qc = useQueryClient();
  const [desde, setDesde] = useState(inicioMes);
  const [hasta, setHasta] = useState(fechaLocal());
  const { data = [], isFetching } = useQuery<Realizado[]>({
    queryKey: ['fin-fx-real', desde, hasta],
    queryFn: () => rpc<Realizado[]>('fin_fx_realizado_pendientes', { _desde: desde, _hasta: hasta }),
  });
  const contabilizar = useMutation({
    mutationFn: () => rpc<any>('fin_fx_realizado_contabilizar', { _desde: desde, _hasta: hasta }),
    onSuccess: (r) => {
      toast.success(r.items ? `${r.items} diferencia(s) contabilizadas — neto ${num(r.neto)}` : (r.mensaje ?? 'Sin diferencias'));
      void qc.invalidateQueries({ queryKey: ['fin-fx-real'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const neto = data.reduce((a, r) => a + Number(r.diferencial), 0);
  const dlg = useDialogoEnLinea();

  return (
    <div className="space-y-4">
      {dlg.ui}
      <p className="text-sm text-muted-foreground">
        Documentos en moneda extranjera que se cobraron o pagaron a una tasa distinta de la del documento.
        La diferencia es resultado del período y se contabiliza en un solo asiento.
      </p>
      <Card><CardContent className="flex flex-wrap items-end gap-3 pt-4">
        <label className="flex flex-col text-xs text-muted-foreground">Desde
          <input type="date" className={`${field} mt-1`} value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
        <label className="flex flex-col text-xs text-muted-foreground">Hasta
          <input type="date" className={`${field} mt-1`} value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
        <Button isLoading={contabilizar.isPending} disabled={!data.length}
          onClick={async () => { if (await dlg.confirmar('Contabilizar el período', { detalle: `Se generará un asiento con ${data.length} diferencia(s), neto ${num(neto)}.` })) contabilizar.mutate(); }}>
          <Calculator className="w-4 h-4 mr-1" /> Contabilizar el período
        </Button>
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary mb-2" />}
      </CardContent></Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">
          Pendientes ({data.length}) — neto <Signo v={neto} />
        </CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">N°</th><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Tercero</th>
              <th className="py-2 pr-3">Documento</th><th className="py-2 pr-3 text-right">Importe</th>
              <th className="py-2 pr-3 text-right">Tasa doc.</th><th className="py-2 pr-3 text-right">Tasa liquidación</th>
              <th className="py-2 pr-3 text-right">Diferencial</th>
            </tr></thead>
            <tbody>
              {data.map((r, i) => (
                <tr key={r.abono_id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-1.5 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-1.5 pr-3">{r.fecha}</td>
                  <td className="py-1.5 pr-3">{r.tercero ?? '—'}<div className="mt-0.5"><Lado lado={r.lado} /></div></td>
                  <td className="py-1.5 pr-3 font-mono text-xs">{r.documento ?? '—'}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(r.monto)} {r.moneda}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(r.tc_doc, 6)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(r.tc_abono ?? r.tc_doc, 6)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums font-semibold"><Signo v={r.diferencial} /></td>
                </tr>
              ))}
              {!data.length && !isFetching && (
                <tr><td colSpan={8} className="py-10 text-center text-muted-foreground">
                  No hay diferencial realizado pendiente en ese período.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ------------------------------------------------------------ revaluación --
function RevaluacionTab() {
  const qc = useQueryClient();
  const [fecha, setFecha] = useState(fechaLocal());
  const { data = [], isFetching } = useQuery<Revaluable[]>({
    queryKey: ['fin-fx-reval', fecha],
    queryFn: () => rpc<Revaluable[]>('fin_fx_revaluacion_previa', { _fecha: fecha }),
  });
  const contabilizar = useMutation({
    mutationFn: () => rpc<any>('fin_fx_revaluacion_contabilizar', { _fecha: fecha }),
    onSuccess: (r) => {
      toast.success(r.items ? `${r.items} saldo(s) revaluados — neto ${num(r.neto)}` : (r.mensaje ?? 'Sin saldos que revaluar'));
      void qc.invalidateQueries({ queryKey: ['fin-fx-reval'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const neto = data.reduce((a, r) => a + Number(r.diferencial), 0);
  const sinTasa = data.filter((r) => r.tasa_cierre === null);
  const dlg = useDialogoEnLinea();

  return (
    <div className="space-y-4">
      {dlg.ui}
      <p className="text-sm text-muted-foreground">
        NIC 21.23: las partidas monetarias en moneda extranjera que siguen abiertas al cierre se revalúan a la
        tasa de esa fecha. Cada revaluación parte de la tasa con la que quedó la anterior, así que sólo asienta
        lo que se movió desde entonces: nunca cuenta dos veces el mismo tramo.
      </p>

      <Card><CardContent className="flex flex-wrap items-end gap-3 pt-4">
        <label className="flex flex-col text-xs text-muted-foreground">Fecha de cierre
          <input type="date" className={`${field} mt-1`} value={fecha} onChange={(e) => setFecha(e.target.value)} /></label>
        <Button isLoading={contabilizar.isPending} disabled={!data.length || sinTasa.length > 0}
          onClick={async () => { if (await dlg.confirmar('Revaluar y contabilizar', { detalle: `Se revaluarán ${data.length} saldo(s) al ${fecha}, neto ${num(neto)}.` })) contabilizar.mutate(); }}>
          <Scale className="w-4 h-4 mr-1" /> Revaluar y contabilizar
        </Button>
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary mb-2" />}
      </CardContent></Card>

      {sinTasa.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
          <span>
            Faltan tasas al {fecha} para: {[...new Set(sinTasa.map((r) => r.moneda))].join(', ')}.
            Regístralas en la pestaña «Tasas» — sin ellas no se puede revaluar.
          </span>
        </div>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">
          Saldos abiertos ({data.length}) — diferencial neto <Signo v={neto} />
        </CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">N°</th><th className="py-2 pr-3">Tercero</th><th className="py-2 pr-3">Documento</th>
              <th className="py-2 pr-3 text-right">Saldo</th><th className="py-2 pr-3 text-right">Tasa anterior</th>
              <th className="py-2 pr-3 text-right">Tasa de cierre</th><th className="py-2 pr-3 text-right">Diferencial</th>
            </tr></thead>
            <tbody>
              {data.map((r, i) => (
                <tr key={r.ticket_id + r.lado} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-1.5 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-1.5 pr-3">{r.tercero ?? '—'}<div className="mt-0.5"><Lado lado={r.lado} /></div></td>
                  <td className="py-1.5 pr-3 font-mono text-xs">{r.documento ?? '—'}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(r.saldo_me)} {r.moneda}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(r.tasa_previa, 6)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">
                    {r.tasa_cierre === null ? <span className="text-amber-600">sin tasa</span> : num(r.tasa_cierre, 6)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums font-semibold"><Signo v={r.diferencial} /></td>
                </tr>
              ))}
              {!data.length && !isFetching && (
                <tr><td colSpan={7} className="py-10 text-center text-muted-foreground">
                  No hay saldos en moneda extranjera abiertos a esa fecha.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ------------------------------------------------------------- pantalla ----
const TABS = [
  { v: 'tasas', l: 'Tasas', icon: ArrowLeftRight },
  { v: 'realizado', l: 'Diferencial realizado', icon: Calculator },
  { v: 'revaluacion', l: 'Revaluación (NIC 21)', icon: Scale },
] as const;

export function TiposCambio() {
  const [tab, setTab] = useState<(typeof TABS)[number]['v']>('tasas');
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold">Tipos de Cambio y Diferencial Cambiario</h1>
      <div className="flex flex-wrap gap-1 border-b">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.v} onClick={() => setTab(t.v)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
                tab === t.v ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
              <Icon className="w-4 h-4" /> {t.l}
            </button>
          );
        })}
      </div>
      {tab === 'tasas' && <Tasas />}
      {tab === 'realizado' && <RealizadoTab />}
      {tab === 'revaluacion' && <RevaluacionTab />}
    </div>
  );
}
